import type { AdminConnectionView, Channel, DataDeletionRequestView, DataDeletionStatus, UserView } from '@hilo/contracts';
import { DATA_DELETION_NOTE_MAX } from '@hilo/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  adminActionError, adminKeys, DELETION_STATUS_LABEL, fetchAdminConnections, fetchDeletionRequests, onboardingFailureText,
  revokeCredential, setConnectionAccess, setConnectionMode, startOnboarding, startInstagramOnboarding, updateDeletionRequest,
} from '../../lib/admin';
import { ApiError } from '../../lib/api';
import { CHANNEL_LABEL, errorText } from '../../lib/copy';
import { fetchUsers, keys } from '../../lib/queries';
import { useCurrentUser } from '../../lib/session';
import { formatAgo } from '../../lib/time';
import { hueOf, initialsOf } from '../../ui/Avatar';
import { ChannelMark } from '../../ui/ChannelMark';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { IconAlert, IconCheck, IconChevron, IconFlag, IconInfo, IconMore, IconPlug, IconPlus, IconShield, IconUnlink, IconUsers } from '../../ui/icons';
import { useNow } from '../../ui/useNow';
import { useSensitiveAction } from '../auth/ReauthDialog';
import { AdminShell } from './AdminShell';
import styles from './Connections.module.css';

export function actionErrorText(error: unknown): string {
  return (error instanceof ApiError && adminActionError(error.code)) || errorText(error);
}

export function formatDateTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('es-SV', {
    timeZone, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date(iso)).replace('.', '');
}

/** Aviso tras volver del alta (`?alta=error&codigo=…` o `?alta=hecha`). */
function OnboardingNotice() {
  const [params, setParams] = useSearchParams();
  const result = params.get('alta');
  if (result !== 'error' && result !== 'hecha') return null;
  const dismiss = () => setParams({}, { replace: true });
  const ok = result === 'hecha';
  return (
    <div className={styles.notice} data-kind={ok ? 'ok' : 'error'} role={ok ? 'status' : 'alert'}>
      <span className={styles.noticeIcon} aria-hidden="true">{ok ? <IconCheck /> : <IconAlert />}</span>
      <p>{ok ? 'Cuenta conectada. Las conexiones nuevas están en pruebas y sin acceso: concédelo a quien vaya a atenderlas.' : onboardingFailureText(params.get('codigo'))}</p>
      <button type="button" className="btn btn-quiet" onClick={dismiss}>Entendido</button>
    </div>
  );
}

const listFormat = new Intl.ListFormat('es', { style: 'long', type: 'conjunction' });

function AccessDialog({ connection, users, onClose }: { connection: AdminConnectionView; users: UserView[]; onClose: () => void }) {
  const client = useQueryClient();
  const { perform, dialog } = useSensitiveAction();
  const [selected, setSelected] = useState(() => new Set(connection.accessUserIds));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const candidates = users.filter((user) => user.role !== 'ADMIN' && (user.active || selected.has(user.id)))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
  const before = new Set(connection.accessUserIds);
  // A quien gane o pierda el acceso se le cierra la sesión: se nombra antes de guardar.
  const changed = candidates.filter((user) => before.has(user.id) !== selected.has(user.id));
  const granted = candidates.filter((user) => selected.has(user.id)).length;
  const toggle = (id: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const done = await perform(() => setConnectionAccess(connection.id, [...selected].filter((id) => candidates.some((user) => user.id === id))));
      if (done === undefined) return;
      await client.invalidateQueries({ queryKey: adminKeys.connections });
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ConfirmDialog open variant="form" icon={<ChannelMark channel={connection.channel} />} title="Quién atiende esta cuenta"
        subtitle={`${CHANNEL_LABEL[connection.channel]}: ${connection.displayName}`}
        confirmLabel="Guardar cambios" busyLabel="Guardando…" confirmDisabled={changed.length === 0}
        busy={busy} onConfirm={() => void save()} onCancel={onClose}>
        <p>Quien tenga acceso verá y atenderá sus conversaciones. Administración las ve todas.</p>
        <fieldset className={styles.people}>
          <legend className={styles.peopleCount} aria-live="polite">
            {candidates.length === 0 ? 'Personas con acceso' : `${granted} de ${candidates.length} ${candidates.length === 1 ? 'persona' : 'personas'} con acceso`}
          </legend>
          {candidates.length === 0 && <p>No hay personas de atención o supervisión activas.</p>}
          {candidates.map((user) => (
            <label key={user.id} className={styles.person}>
              <input type="checkbox" checked={selected.has(user.id)} onChange={() => toggle(user.id)} disabled={busy} />
              <span className={styles.face} style={{ '--hue': hueOf(user.id) } as CSSProperties} aria-hidden="true">{initialsOf(user.displayName)}</span>
              <span className={styles.personName}>{user.displayName}</span>
              <span className={styles.personRole}>{user.role === 'SUPERVISOR' ? 'Supervisión' : 'Atención'}{user.active ? '' : ' · desactivada'}</span>
            </label>
          ))}
        </fieldset>
        {changed.length > 0
          ? (
            <p className={styles.logoutNote}>
              <IconAlert />
              <span>Al guardar se cerrará la sesión de {listFormat.format(changed.map((user) => user.displayName))} para aplicar el cambio.</span>
            </p>
          )
          : <p className={styles.small}>A quien gane o pierda el acceso se le cierra la sesión para aplicar el cambio.</p>}
        {error && <p className={styles.dialogError} role="alert">{error}</p>}
      </ConfirmDialog>
      {dialog}
    </>
  );
}

function RevokeDialog({ connection, onClose }: { connection: AdminConnectionView; onClose: () => void }) {
  const client = useQueryClient();
  const { perform, dialog } = useSensitiveAction();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      const done = await perform(() => revokeCredential(connection.id));
      if (done === undefined) return;
      await client.invalidateQueries({ queryKey: adminKeys.connections });
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ConfirmDialog open icon={<IconUnlink />} title={`¿Desconectar ${connection.displayName}?`} confirmLabel="Desconectar" busyLabel="Desconectando…"
        busy={busy} onConfirm={() => void revoke()} onCancel={onClose}>
        <p>{connection.instagramLogin === 'INSTAGRAM_LOGIN' ? 'Hilo desconecta esta cuenta de Instagram y borra su autorización guardada.' : 'Hilo borra el token de la página y desactiva su Messenger y su Instagram.'} Las conversaciones se conservan.</p>
        <p>Para volver a usarla, conéctala otra vez. El permiso en Meta se retira desde la configuración de apps de Facebook o Instagram.</p>
        {error && <p className={styles.dialogError} role="alert">{error}</p>}
      </ConfirmDialog>
      {dialog}
    </>
  );
}

/**
 * Pruebas → producción. En producción Hilo contesta a cualquier cliente, no solo a las cuentas de prueba.
 * El camino inverso existe en la API, pero el panel no lo ofrece: confundía y no tiene uso diario.
 */
function PromoteDialog({ connection, onClose }: { connection: AdminConnectionView; onClose: () => void }) {
  const client = useQueryClient();
  const { perform, dialog } = useSensitiveAction();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const change = async () => {
    setBusy(true);
    setError(null);
    try {
      const done = await perform(() => setConnectionMode(connection.id, 'PRODUCTION'));
      if (done === undefined) return;
      await Promise.all([client.invalidateQueries({ queryKey: adminKeys.connections }), client.invalidateQueries({ queryKey: keys.connections })]);
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ConfirmDialog open icon={<IconFlag />} title={`¿Activar ${connection.displayName}?`}
        confirmLabel="Activar" busyLabel="Activando…" busy={busy} onConfirm={() => void change()} onCancel={onClose}>
        <p>Hilo podrá responder a cualquier persona que escriba a esta cuenta, no solo a las cuentas de prueba. Vale desde la siguiente respuesta.</p>
        <p>Hazlo solo cuando Meta haya aprobado la revisión de la app Hilo.</p>
        {error && <p className={styles.dialogError} role="alert">{error}</p>}
      </ConfirmDialog>
      {dialog}
    </>
  );
}

type Tone = 'ok' | 'test' | 'idle' | 'warning' | 'off';
type Health = { tone: Tone; text: string; hint?: string };

/** Una sola respuesta a «¿funciona esta cuenta?», de lo más grave a lo normal, en palabras de quien la usa. */
function healthOf(connection: AdminConnectionView): Health {
  if (!connection.enabled) return { tone: 'off', text: 'Desactivada' };
  if (connection.origin === 'CONSENT' && !connection.credentialsPresent) return { tone: 'off', text: 'Desconectada', hint: 'Conéctala otra vez para usarla' };
  if (!connection.credentialsPresent) return { tone: 'warning', text: 'Sin credencial', hint: 'Falta la credencial en el servidor: no puede responder' };
  // Instagram Login: el token dura 60 días y lo renueva el trabajador.
  if (connection.tokenRefreshFailure === 'TOKEN_EXPIRED') return { tone: 'off', text: 'Autorización vencida', hint: 'Conéctala otra vez' };
  if (connection.tokenRefreshFailure) return { tone: 'warning', text: 'Renovando autorización', hint: 'Falló la renovación; se reintentará sola' };
  if (connection.mode === 'TEST') return { tone: 'test', text: 'En prueba', hint: 'Solo responde a las cuentas de prueba' };
  if (!connection.lastWebhookAt) return { tone: 'idle', text: 'Sin actividad aún' };
  return { tone: 'ok', text: 'Activa' };
}

/** Quién la atiende: iniciales apiladas; si nadie tiene acceso, solo administración la ve. */
function AccessPeople({ people }: { people: Array<{ id: string; name: string }> }) {
  if (people.length === 0) return <span className={styles.adminOnly}>Solo administración</span>;
  const shown = people.slice(0, 3);
  const full = listFormat.format(people.map((person) => person.name));
  return (
    <span className={styles.who} title={full}>
      <span className={styles.stack} aria-hidden="true">
        {shown.map((person) => (
          <span key={person.id} className={styles.face} style={{ '--hue': hueOf(person.id) } as CSSProperties}>{initialsOf(person.name)}</span>
        ))}
        {people.length > shown.length && <span className={styles.face} data-more>+{people.length - shown.length}</span>}
      </span>
      <span className="visually-hidden">La atienden {full}</span>
    </span>
  );
}

type MenuAction = { label: string; icon: ReactNode; onSelect: () => void; danger?: boolean };

/** Menú «⋯» de una fila: se cierra con Escape, con clic fuera o al elegir. */
function RowMenu({ name, actions }: { name: string; actions: MenuAction[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return;
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  return (
    <div className={styles.menuWrap} ref={ref}>
      <button type="button" className={styles.menuButton} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}
        aria-label={`Opciones de ${name}`} onClick={() => setOpen((value) => !value)}>
        <IconMore />
      </button>
      {open && (
        <div className={styles.menu} role="menu" id={menuId} aria-label={`Opciones de ${name}`}>
          {actions.map((action) => (
            <button key={action.label} type="button" role="menuitem" className={styles.menuItem} data-danger={action.danger || undefined}
              onClick={() => { setOpen(false); action.onSelect(); }}>
              {action.icon}{action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Lo técnico de una cuenta: de dónde sale su credencial, cuándo se conectó y el último aviso exacto de Meta. */
function ConnectionDetails({ connection, timeZone }: { connection: AdminConnectionView; timeZone: string }) {
  const consent = connection.origin === 'CONSENT';
  const items: Array<[string, string]> = [
    ['Canal', CHANNEL_LABEL[connection.channel]],
    ['Origen', !consent
      ? 'Configurada en el servidor'
      : connection.grantedAt && connection.grantedBy
        ? `Conectada con Meta por ${connection.grantedBy.displayName} el ${formatDateTime(connection.grantedAt, timeZone)}`
        : 'Conectada con Meta y desconectada después'],
    ['Modo', connection.mode === 'TEST' ? 'Pruebas: solo responde a las cuentas de prueba' : 'Producción: responde a cualquier cliente'],
  ];
  if (connection.instagramLogin === 'INSTAGRAM_LOGIN') items.push(['Tipo', 'Instagram Login: solo texto']);
  if (connection.tokenExpiresAt) items.push(['Autorización vigente hasta', formatDateTime(connection.tokenExpiresAt, timeZone)]);
  items.push(['Último aviso de Meta', connection.lastWebhookAt ? formatDateTime(connection.lastWebhookAt, timeZone) : 'Todavía ninguno']);
  return (
    <dl className={styles.details}>
      {items.map(([term, value]) => (
        <div key={term}><dt>{term}</dt><dd>{value}</dd></div>
      ))}
    </dl>
  );
}

function ConnectionRow({ connection, users, timeZone, now, onAccess, onRevoke, onPromote }: {
  connection: AdminConnectionView; users: UserView[]; timeZone: string; now: number; onAccess: () => void; onRevoke: () => void; onPromote: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const people = connection.accessUserIds.map((id) => ({ id, name: users.find((user) => user.id === id)?.displayName ?? 'Persona sin nombre' }));
  const health = healthOf(connection);
  // WhatsApp sigue en pruebas hasta tener el número de la empresa y el envío por BSUID (el servidor también lo impide).
  const canPromote = connection.mode === 'TEST' && connection.enabled && connection.channel !== 'WHATSAPP';
  const canRevoke = connection.origin === 'CONSENT' && connection.credentialsPresent;
  const actions: MenuAction[] = [
    { label: 'Quién atiende', icon: <IconUsers />, onSelect: onAccess },
    { label: showDetails ? 'Ocultar detalles' : 'Ver detalles', icon: <IconInfo />, onSelect: () => setShowDetails((value) => !value) },
  ];
  if (canPromote) actions.push({ label: 'Activar para clientes', icon: <IconFlag />, onSelect: onPromote });
  if (canRevoke) actions.push({ label: 'Desconectar', icon: <IconUnlink />, onSelect: onRevoke, danger: true });
  return (
    <li className={styles.row}>
      <div className={styles.rowLine}>
        <ChannelMark channel={connection.channel} />
        <div className={styles.account}>
          <h3 className={styles.rowName}>{connection.displayName}</h3>
          <span className={styles.channel}>{CHANNEL_LABEL[connection.channel]}</span>
        </div>
        <div className={styles.status}>
          <span className={styles.pill} data-tone={health.tone} title={health.hint}>{health.text}</span>
          {health.hint && health.tone !== 'test' && <span className={styles.hint}>{health.hint}</span>}
          {connection.lastWebhookAt && connection.enabled && (
            <span className={styles.activity}>
              Actividad{' '}
              <time dateTime={connection.lastWebhookAt} title={formatDateTime(connection.lastWebhookAt, timeZone)}>
                {formatAgo(connection.lastWebhookAt, timeZone, new Date(now))}
              </time>
            </span>
          )}
        </div>
        <AccessPeople people={people} />
        <RowMenu name={connection.displayName} actions={actions} />
      </div>
      {showDetails && <div className={styles.detailsWrap}><ConnectionDetails connection={connection} timeZone={timeZone} /></div>}
    </li>
  );
}

const CHANNEL_ORDER: Channel[] = ['MESSENGER', 'INSTAGRAM', 'WHATSAPP'];
const byChannelAndName = (a: AdminConnectionView, b: AdminConnectionView) =>
  CHANNEL_ORDER.indexOf(a.channel) - CHANNEL_ORDER.indexOf(b.channel) || a.displayName.localeCompare(b.displayName, 'es');

/** Botón único para conectar: el menú dice qué conecta cada opción. */
function ConnectMenu({ starting, onMeta, onInstagram }: { starting: boolean; onMeta: () => void; onInstagram: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return;
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  const pick = (action: () => void) => () => { setOpen(false); action(); };
  return (
    <div className={styles.connectWrap} ref={ref}>
      <button type="button" className={`btn btn-primary ${styles.connect}`} disabled={starting} aria-busy={starting}
        aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => setOpen((value) => !value)}>
        <IconPlus />{starting ? 'Abriendo Meta…' : 'Conectar cuenta'}<IconChevron className={styles.chevron} />
      </button>
      {open && (
        <div className={`${styles.menu} ${styles.connectMenu}`} role="menu" id={menuId} aria-label="Conectar cuenta">
          <button type="button" role="menuitem" className={styles.connectItem} onClick={pick(onMeta)}>
            <ChannelMark channel="MESSENGER" />
            <span><strong>Facebook e Instagram</strong><small>Una página de Facebook y su Instagram vinculado</small></span>
          </button>
          <button type="button" role="menuitem" className={styles.connectItem} onClick={pick(onInstagram)}>
            <ChannelMark channel="INSTAGRAM" />
            <span><strong>Solo Instagram</strong><small>Con el inicio de sesión de Instagram</small></span>
          </button>
          <Link to="/conexiones/whatsapp" role="menuitem" className={styles.connectItem} onClick={() => setOpen(false)}>
            <ChannelMark channel="WHATSAPP" />
            <span><strong>WhatsApp</strong><small>Un número de WhatsApp Business</small></span>
          </Link>
        </div>
      )}
    </div>
  );
}

function RowsSkeleton() {
  return (
    <ul className={styles.panel} aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <li key={index} className={`${styles.row} ${styles.bone}`}><span /><span><span /><span /></span></li>
      ))}
    </ul>
  );
}

function DeletionDialog({ request, admins, onClose }: { request: DataDeletionRequestView; admins: UserView[]; onClose: () => void }) {
  const client = useQueryClient();
  const ids = { responsible: useId(), status: useId(), note: useId() };
  const [responsibleId, setResponsibleId] = useState(request.responsible?.id ?? '');
  const [status, setStatus] = useState<DataDeletionStatus>(request.status);
  const [note, setNote] = useState(request.publicNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateDeletionRequest(request.id, { responsibleId: responsibleId || null, status, publicNote: note.trim() || null });
      await client.invalidateQueries({ queryKey: adminKeys.deletionRequests });
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog open icon={<IconShield />} title={`Solicitud ${request.confirmationCode}`} confirmLabel="Guardar" busyLabel="Guardando…"
      busy={busy} onConfirm={() => void save()} onCancel={onClose}>
      <div className={styles.form}>
        <label className="field" htmlFor={ids.responsible}>
          <span>Responsable</span>
          <select id={ids.responsible} className="input" value={responsibleId} disabled={busy} onChange={(event) => setResponsibleId(event.target.value)}>
            <option value="">Sin asignar</option>
            {admins.map((admin) => <option key={admin.id} value={admin.id}>{admin.displayName}</option>)}
          </select>
        </label>
        <label className="field" htmlFor={ids.status}>
          <span>Estado</span>
          <select id={ids.status} className="input" value={status} disabled={busy} onChange={(event) => setStatus(event.target.value as DataDeletionStatus)}>
            {(Object.keys(DELETION_STATUS_LABEL) as DataDeletionStatus[]).map((value) => <option key={value} value={value}>{DELETION_STATUS_LABEL[value]}</option>)}
          </select>
        </label>
        <label className="field" htmlFor={ids.note}>
          <span>Nota pública</span>
          <textarea id={ids.note} className={`input ${styles.textarea}`} value={note} maxLength={DATA_DELETION_NOTE_MAX} disabled={busy}
            onChange={(event) => setNote(event.target.value)} />
        </label>
        <p className={styles.small}>La ve quien consulte el código. Sin datos personales. Si se conservan datos, explica cuáles y por qué.</p>
      </div>
      {error && <p className={styles.dialogError} role="alert">{error}</p>}
    </ConfirmDialog>
  );
}

function DeletionRequests({ users, timeZone }: { users: UserView[]; timeZone: string }) {
  const requests = useQuery({ queryKey: adminKeys.deletionRequests, queryFn: fetchDeletionRequests });
  const [editing, setEditing] = useState<DataDeletionRequestView | null>(null);
  const admins = users.filter((user) => user.role === 'ADMIN' && user.active);
  return (
    <section className={styles.section} aria-labelledby="solicitudes">
      <div className={styles.sectionHead}>
        <h2 id="solicitudes" className={styles.sectionTitle}>Solicitudes de eliminación de datos</h2>
        <p className={styles.sectionLead}>
          Llegan de Meta cuando alguien que autorizó una página pide borrar sus datos. Hilo borra solo sus tokens y su identificador;
          el resto lo revisa la persona responsable.
        </p>
      </div>
      {requests.isPending && <p className={styles.muted}>Cargando solicitudes…</p>}
      {requests.isError && <p className={styles.muted} role="alert">{errorText(requests.error)}</p>}
      {requests.data?.length === 0 && (
        <div className={styles.emptyPanel}>
          <span className={styles.emptyIcon} aria-hidden="true"><IconShield /></span>
          <p>No hay solicitudes. Cuando llegue una, aparecerá aquí con su código de confirmación.</p>
        </div>
      )}
      {requests.data && requests.data.length > 0 && (
        <ul className={styles.panel}>
          {requests.data.map((request) => (
            <li key={request.id} className={styles.request}>
              <span className={styles.code}>{request.confirmationCode}</span>
              <span className={styles.requestStatus} data-tone={request.status === 'IN_PROGRESS' ? 'warning' : 'ok'}>{DELETION_STATUS_LABEL[request.status]}</span>
              <span className={styles.muted}>Recibida el {formatDateTime(request.receivedAt, timeZone)}</span>
              <span className={styles.muted}>{request.responsible ? `Responsable: ${request.responsible.displayName}` : 'Sin responsable'}</span>
              <button type="button" className={`btn btn-secondary ${styles.compact}`} onClick={() => setEditing(request)}>
                Gestionar<span className="visually-hidden"> la solicitud {request.confirmationCode}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {editing && <DeletionDialog request={editing} admins={admins} onClose={() => setEditing(null)} />}
    </section>
  );
}

/** Conexiones (solo ADMIN): qué cuentas atiende Hilo, si funcionan y quién las atiende. */
export function ConnectionsPage() {
  const user = useCurrentUser();
  const connections = useQuery({ queryKey: adminKeys.connections, queryFn: fetchAdminConnections });
  const users = useQuery({ queryKey: keys.users, queryFn: fetchUsers });
  const { perform, dialog } = useSensitiveAction();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [access, setAccess] = useState<AdminConnectionView | null>(null);
  const [revoking, setRevoking] = useState<AdminConnectionView | null>(null);
  const [promoting, setPromoting] = useState<AdminConnectionView | null>(null);
  const people = users.data ?? [];
  const now = useNow(60_000);

  const connect = async (instagram = false) => {
    setStarting(true);
    setStartError(null);
    try {
      const view = await perform(instagram ? startInstagramOnboarding : startOnboarding);
      if (!view) return setStarting(false);
      // El diálogo oficial; el backend valida el retorno y redirige dentro de Conexiones.
      window.location.assign(view.authorizeUrl);
    } catch (error) {
      setStartError(actionErrorText(error));
      setStarting(false);
    }
  };

  const data = connections.data ?? [];
  const active = data.filter((connection) => connection.enabled).sort(byChannelAndName);
  const disabled = data.filter((connection) => !connection.enabled).sort(byChannelAndName);
  const tones = active.map((connection) => healthOf(connection).tone);
  const working = tones.filter((tone) => tone === 'ok' || tone === 'idle').length;
  const testing = tones.filter((tone) => tone === 'test').length;
  const problems = tones.filter((tone) => tone === 'warning' || tone === 'off').length;
  const summary = [
    working > 0 && `${working} ${working === 1 ? 'activa' : 'activas'}`,
    testing > 0 && `${testing} en prueba`,
    disabled.length > 0 && `${disabled.length} ${disabled.length === 1 ? 'desactivada' : 'desactivadas'}`,
  ].filter(Boolean).join(' · ');
  const row = (connection: AdminConnectionView) => (
    <ConnectionRow key={connection.id} connection={connection} users={people} timeZone={user.timezone} now={now}
      onAccess={() => setAccess(connection)} onRevoke={() => setRevoking(connection)} onPromote={() => setPromoting(connection)} />
  );

  return (
    <AdminShell>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Conexiones</h1>
          <p className={styles.lead}>Las cuentas de Messenger, Instagram y WhatsApp que atiende Hilo.</p>
          {data.length > 0 && (
            <p className={styles.summary}>
              <span>{summary}</span>
              {problems > 0 && <span className={styles.summaryWarn}>{problems === 1 ? '1 necesita atención' : `${problems} necesitan atención`}</span>}
            </p>
          )}
        </div>
        <ConnectMenu starting={starting} onMeta={() => void connect()} onInstagram={() => void connect(true)} />
      </header>
      {startError && <div className={styles.notice} data-kind="error" role="alert"><span className={styles.noticeIcon} aria-hidden="true"><IconAlert /></span><p>{startError}</p></div>}
      <OnboardingNotice />
      {connections.isPending && <><p className="visually-hidden" role="status">Cargando conexiones…</p><RowsSkeleton /></>}
      {connections.isError && <p className={styles.muted} role="alert">{errorText(connections.error)}</p>}
      {connections.isSuccess && data.length === 0 && (
        <div className={styles.emptyPanel}>
          <span className={styles.emptyIcon} aria-hidden="true"><IconPlug /></span>
          <p>Todavía no hay cuentas. Usa «Conectar cuenta» para empezar a recibir sus mensajes aquí.</p>
        </div>
      )}
      {active.length > 0 && (
        <section className={styles.section} aria-label="Cuentas conectadas">
          <div className={styles.listHead} aria-hidden="true">
            <span>Cuenta</span><span>Estado</span><span>Atienden</span><span />
          </div>
          <ul className={styles.panel}>{active.map(row)}</ul>
        </section>
      )}
      {disabled.length > 0 && (
        <details className={styles.disabledGroup}>
          <summary><IconChevron className={styles.chevron} />Desactivadas ({disabled.length})</summary>
          <ul className={styles.panel}>{disabled.map(row)}</ul>
        </details>
      )}
      <DeletionRequests users={people} timeZone={user.timezone} />
      {access && <AccessDialog connection={access} users={people} onClose={() => setAccess(null)} />}
      {revoking && <RevokeDialog connection={revoking} onClose={() => setRevoking(null)} />}
      {promoting && <PromoteDialog connection={promoting} onClose={() => setPromoting(null)} />}
      {dialog}
    </AdminShell>
  );
}
